<template>
  <!-- fitScreenPage / fitScreenFill (MainLayout fillScreen): the card is at least as tall as the space left on the
       screen; a longer list grows it and the whole page scrolls -->
  <div class="fitScreenPage">
    <div class="fitScreenFill overflow-hidden rounded-2xl w-full flex flex-col">
      <div class="laptop:flex-1 mobile:flex flex-col-reverse laptop:flex-row overflow-x-auto laptop:!rounded-[20px] laptop:rounded-r-none laptop:border border-[#CCCCCC]">
        <div class="laptop:flex-1 laptop:min-w-0 laptop:w-full mt-2.5 laptop:mt-0 rounded-[20px] mobile:border laptop:!border-0 border-[#CCCCCC]">
          <div
            class="flex mobile:flex-col tablet:flex-row tablet:items-center justify-between p-[10px] border-b !border-gray-300 bg-white/50 rounded-ss-[20px] laptop:rounded-ss-0">
            <!-- below laptop the date is already in the calendar bar, so only the count shows here -->
            <div class="mobile:hidden laptop:flex items-center gap-2">
              <img src="../../assets/images/dashboard-calendar-icon2.png" alt="Calendar Icon"
                class="w-[45px] h-[45px] object-cover">
              <div>
                <h3 class="m-0 text-[16px] largeDesktop:text-[18px] font-roboto font-medium text-purple-900">
                  {{ displayDateTitle }}
                </h3>
                <p class="m-0 text-[13px] largeDesktop:text-[14px] font-roboto font-medium text-gray-600">
                  {{ displayDayOfWeek }}
                </p>
              </div>
            </div>
            <p class="m-0 text-[14px] font-roboto font-normal text-[#666666]">
              {{ activitiesCountText }}
            </p>
          </div>
          <div class="p-[5px] laptop:p-2">
            <div v-if="tableItems.length > 0" class="">
              <customTable :headers="tableHeaders" :items="tableItems" :page="page" :per-page="limit" :row-to="rowRoute" />
              <!-- client-side paging of the day's list (calendar/day has no paging); same pager as the Work Order listings -->
              <div v-if="tableItems.length > limit"
                class="flex mobile:flex-col tablet:flex-row mobile:gap-3 tablet:gap-0 items-center justify-between pt-[10px] px-1">
                <paginationEntries class="mobile:hidden tablet:block" :current-page="page" :per-page="limit"
                  :total-items="tableItems.length" />
                <customPagination :page="page" :total_pages="totalPages" :update-page="updatePage" />
              </div>
            </div>
            <div v-else class="min-h-[300px] laptop:min-h-[420px] flex items-center justify-center">
              <!-- a failed calendar/day must not claim the day is empty (QA D-51) -->
              <div v-if="dayError && !isLoading" class="flex flex-col items-center gap-[12px] text-center">
                <p class="m-0 font-normal font-roboto text-[20px] tablet:text-[25px] text-[#999999]">Couldn't load activities</p>
                <button type="button" class="fxBtnPrimary" @click="fetchDay(selectedDate)">Retry</button>
              </div>
              <EmptyState v-else-if="!isLoading" :title="emptyStateText" text="" min-height="" />
            </div>
          </div>
        </div>
        <div class="laptop:max-w-[290px] largeDesktop:max-w-[330px] laptop:min-w-[290px] largeDesktop:min-w-[330px] laptop:flex-shrink-0 mobile:border laptop:border-0 laptop:!border-l border-[#CCCCCC] rounded-[20px] laptop:rounded-l-none overflow-hidden">
          <!-- Below laptop the calendar folds into a bar (selected date); tap to open, picking a day closes it -->
          <button type="button" class="laptop:hidden w-full flex items-center justify-between gap-3 px-[14px] py-[10px] bg-white/60 text-left"
            :aria-expanded="calendarOpen" @click="calendarOpen = !calendarOpen">
            <span class="flex items-center gap-[10px] min-w-0">
              <img src="../../assets/images/dashboard-calendar-icon.png" alt="" class="w-[32px] h-[32px] object-cover flex-shrink-0">
              <span class="min-w-0">
                <span class="block text-[15px] font-roboto font-medium text-purple-900 truncate">{{ selectedDateShort }}</span>
                <span class="block text-[12px] font-roboto text-[#666666]">{{ calendarOpen ? 'Tap to close calendar' : 'Tap to change date' }}</span>
              </span>
            </span>
            <span class="w-[30px] h-[30px] rounded-full bg-white shadow-custom flex items-center justify-center flex-shrink-0 transition-transform duration-300"
              :class="{ 'rotate-180': calendarOpen }">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="#F1C93B" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
            </span>
          </button>
          <!-- grid-rows 0fr/1fr animates the height; laptop+ always open -->
          <div class="grid transition-[grid-template-rows] duration-300 ease-out laptop:!grid-rows-[1fr]"
            :class="calendarOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'">
            <div class="min-h-0 overflow-hidden">
              <customCalendar :events="calendarEvents" :loading="monthLoading" @date-selected="onDateSelected" @month-changed="onMonthChanged" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import customCalendar from '../../components/partialComponent/customCalendar.vue';
import customTable from "../../components/partialComponent/customTable.vue";
import customPagination from "../../components/partialComponent/customPagination.vue";
import paginationEntries from "../../components/partialComponent/pagination-entries.vue";
import EmptyState from "../../components/partialComponent/EmptyState.vue";
import axios from "axios";
import moment from "moment";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  components: {
    customTable,
    customCalendar,
    customPagination,
    paginationEntries,
    EmptyState,
  },
  data() {
    return {
      selectedDate: sessionStorage.getItem('dashboard_selected_date') || moment().format("MM-DD-YYYY"),
      // calendar/month-days: MM-DD-YYYY → true when that day has at least one job (calendar dots)
      monthDays: {},
      // calendar/day: the selected day's jobs
      dayItems: [],
      page: 1,
      limit: 25,
      // mobile / tablet: calendar folded into a bar until tapped
      calendarOpen: false,
      lastFetchedMonth: '',
      // calendar/month-days in flight (spinner on the calendar)
      monthLoading: false,
      // latest calendar/day request, so a slow answer for a day clicked earlier can't replace the current one
      dayRequest: 0,
      // Hides the empty state while the day is loading (it flashed before data arrived)
      isLoading: false,
      dayError: false,
      tableHeaders: [
        { key: 'id', text: 'ID' },
        { key: 'customerName', text: 'Consumer Name' },
        { key: 'servicetype', text: 'Service Type' },
        { key: 'date', text: 'Date', sortKey: 'datetime' },
        { key: 'textStatus', text: 'Status' },
      ],
    }
  },
  computed: {
    selectedDateShort() {
      return moment(this.selectedDate, "MM-DD-YYYY").format("ddd, MMM DD, YYYY");
    },
    tableItems() {
      return this.dayItems.map(item => {
        return {
          id: item.id,
          type: item.type || '',
          customerName: item.consumer_name || '',
          servicetype: item.type_text || '',
          datetime: item.datetime || 0,
          date: item.datetime ? moment.unix(item.datetime).format("MM/DD/YYYY hh:mm A") : '',
          time: item.datetime ? moment.unix(item.datetime).format("hh:mm A") : '',
          textStatus: item.status_text || '',
          image: (item.depp_schedule && item.depp_schedule.company_logo)
            ? item.depp_schedule.company_logo
            : null
        };
      });
    },
    totalPages() {
      return Math.ceil(this.tableItems.length / this.limit);
    },
    activitiesCountText() {
      const count = this.tableItems.length;
      // not "no activities" while calendar/day is still loading (QA D-43)
      if (this.isLoading) {
        return "Loading activities…";
      } else if (this.dayError) {
        return "Activities couldn't be loaded";
      } else if (count === 0) {
        return "You have no activities";
      } else if (count === 1) {
        return "You have 1 activity";
      } else {
        return `You have ${count} activities`;
      }
    },
    displayDateTitle() {
      return moment(this.selectedDate, "MM-DD-YYYY").format("MMMM DD, YYYY");
    },
    emptyStateText() {
      const day = moment(this.selectedDate, "MM-DD-YYYY");
      return day.isSame(moment(), 'day')
        ? "No Activities Today"
        : `No Activities on ${day.format("MMMM DD, YYYY")}`;
    },
    displayDayOfWeek() {
      return moment(this.selectedDate, "MM-DD-YYYY").format("dddd");
    },
    calendarEvents() {
      return Object.keys(this.monthDays).filter(dateStr => this.monthDays[dateStr] === true);
    }
  },
  watch: {
    // day reloaded with fewer rows → back to the first page
    totalPages(n) {
      if (this.page > Math.max(n, 1)) this.page = 1;
    },
  },
  created() {
    const savedDate = sessionStorage.getItem('dashboard_selected_date') || moment().format("MM-DD-YYYY");
    const savedMoment = moment(savedDate, "MM-DD-YYYY");
    // after login / on open: dots for the selected (by default current) month + that day's list, in parallel
    this.fetchMonthDays(savedMoment.format("YYYY-MM-01"));
    this.fetchDay(savedDate);
  },
  beforeUnmount() {
    clearTimeout(this.monthTimer);
    clearTimeout(this.dayTimer);
    if (this.monthAbort) this.monthAbort.abort();
  },
  methods: {
    // a clicked day, or the same day in a month the user moved to (fromMonthChange)
    onDateSelected(formattedDate, { fromMonthChange = false } = {}) {
      this.selectedDate = formattedDate;
      this.page = 1;
      // Keep the picked day when coming back from a detail page
      sessionStorage.setItem('dashboard_selected_date', formattedDate);
      clearTimeout(this.dayTimer);
      if (fromMonthChange) {
        // fast month clicking: only the day the user stops on is fetched
        this.dayTimer = setTimeout(() => this.fetchDay(formattedDate), 300);
        return;
      }
      this.fetchDay(formattedDate);
      // below laptop, close the calendar so the day's list is in view
      if (window.innerWidth < 1024) this.calendarOpen = false;
    },
    updatePage(page) {
      this.page = page;
    },
    // row → detail route (customTable renders it as a real link, so it opens in a new tab too)
    rowRoute(item) {
      if (!item || !item.id) return null;
      const type = (item.type || '').toLowerCase();
      if (type === 'appointment') return { name: 'AppointmentDetail', params: { id: item.id } };
      if (type === 'universal_quote') return { name: 'QuoteDetail', params: { id: item.id } };
      if (type === 'repair_service') return { name: 'job-detail', params: { id: item.id } };
      return null;
    },
    // month change: dots only (the list stays on the selected day). Fast clicking through months: wait until the
    // user stops (300ms) and cancel the request of a month already left, so only the month on screen is fetched.
    onMonthChanged(dateStr) {
      clearTimeout(this.monthTimer);
      if (this.monthDaysLoaded(dateStr)) {
        this.cancelMonthDays();
        return;
      }
      this.monthLoading = true;
      this.monthTimer = setTimeout(() => this.fetchMonthDays(dateStr), 300);
    },
    // every day of that month already has its flag (fetched before)
    monthDaysLoaded(dateStr) {
      const key = moment(dateStr, "YYYY-MM-DD").format("MM-01-YYYY");
      return key in this.monthDays;
    },
    cancelMonthDays() {
      if (this.monthAbort) this.monthAbort.abort();
      this.monthAbort = null;
      this.monthLoading = false;
    },
    // G-46 calendar/month-days {date: any day of the month (Y-m-d), time_offset}: every day of the month → bool
    async fetchMonthDays(dateStr) {
      if (this.lastFetchedMonth === dateStr && this.monthAbort) return;
      this.cancelMonthDays();
      const controller = new AbortController();
      this.monthAbort = controller;
      this.lastFetchedMonth = dateStr;
      this.monthLoading = true;
      try {
        const response = await axios.get('/calendar/month-days', {
          params: { date: dateStr, time_offset: new Date().getTimezoneOffset() },
          signal: controller.signal,
        });
        if (response.data && response.data.status) {
          this.monthDays = { ...this.monthDays, ...(response.data.body || {}) };
        } else {
          toast.error(response.data.message || "Failed to fetch calendar");
        }
      } catch (error) {
        if (axios.isCancel(error) || error?.name === 'CanceledError') return;
        console.error("Error fetching calendar days:", error);
        toast.error("An error occurred while fetching calendar data.");
      } finally {
        if (this.monthAbort === controller) {
          this.monthAbort = null;
          this.monthLoading = false;
        }
      }
    },
    // G-47 calendar/day {date: Y-m-d, time_offset}: that day's jobs (same items as one day of calendar/monthly)
    async fetchDay(formattedDate) {
      const request = ++this.dayRequest;
      this.isLoading = true;
      this.dayError = false;
      this.dayItems = [];
      if (this.$loader && this.$loader.show) {
        this.$loader.show();
      }
      try {
        const response = await axios.get('/calendar/day', {
          params: {
            date: moment(formattedDate, "MM-DD-YYYY").format("YYYY-MM-DD"),
            time_offset: new Date().getTimezoneOffset()
          }
        });
        if (request !== this.dayRequest) return;
        if (response.data && response.data.status) {
          this.dayItems = Array.isArray(response.data.body) ? response.data.body : [];
        } else {
          this.dayError = true;
        }
      } catch (error) {
        if (request !== this.dayRequest) return;
        console.error("Error fetching day activities:", error);
        this.dayError = true;
      } finally {
        if (request === this.dayRequest) {
          this.isLoading = false;
          if (this.$loader && this.$loader.hide) {
            this.$loader.hide();
          }
        }
      }
    }
  }
};
</script>
